// Trade history (SM-829): one row per side of each fill of the node's
// users, with its price, quantity, fees and ledger status, in the selected
// currency — the side's cash leg: CASH is GOLD, a GAME buy RED and a GAME
// sell GREEN, so a GAME user's sell orders list under RED but their sell
// fills under GREEN. A period of whole local days: the last 30 by default,
// at most 92, always sent as an explicit from_unix_ms / to_unix_ms. Direct
// or subtree as on every Trading page; the User picker reads one account's
// own route. Does not poll.
//
//   GET /api/broker/{id}/trades?scope=&currency=&from_unix_ms=&to_unix_ms=&offset=&limit=
//   GET /api/broker/{id}/accounts/{aid}/trades?currency=&from_unix_ms=&to_unix_ms=&offset=&limit=
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { EmptyPage, Pager } from "@/components/ui/Common";
import { QueryBlock } from "@/components/ui/States";
import { TradesTable } from "@/components/TradesTable";
import {
  TRADING_PAGE_LIMIT,
  TradingFilters,
  WindowPicker,
  validateTradingSearch,
  validateWindowSearch,
  type TradingSearch,
  type WindowSearch,
} from "@/components/TradingFilters";
import { fetchAccountTrades, fetchTrades, keys } from "@/lib/queries";
import { resolveWindow, scopeFromSearch } from "@/lib/trading";
import type { AccountScope, SettlementTradeListResponse } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

type TradesSearch = TradingSearch & WindowSearch;

export const Route = createFileRoute("/_app/trades")({
  validateSearch: (s: Record<string, unknown>): TradesSearch => ({ ...validateTradingSearch(s), ...validateWindowSearch(s) }),
  beforeLoad: capabilityGuard("VIEW"),
  component: TradeHistoryPage,
});

function TradeHistoryPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId, currency } = useScope();
  const scope: AccountScope = scopeFromSearch(search.scope);
  const account = search.account;
  const now = Date.now();
  const w = resolveWindow(search.from, search.to, now);
  const page = { currency, from_unix_ms: w.from, to_unix_ms: w.to, offset: search.offset ?? 0, limit: TRADING_PAGE_LIMIT };
  const nodeParams = { scope, ...page };
  const probe: { queryKey: QueryKey; queryFn: () => Promise<SettlementTradeListResponse> } = account
    ? { queryKey: keys.accountTrades(nodeId, account, page), queryFn: () => fetchAccountTrades(nodeId, account, page) }
    : { queryKey: keys.trades(nodeId, nodeParams), queryFn: () => fetchTrades(nodeId, nodeParams) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const set = (patch: Partial<TradesSearch>) => void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }) });
  const onOffset = (o: number) => set({ offset: o || undefined });
  const scopeHint = t(account ? "trading.emptyHintAccount" : "trading.emptyHintNode");
  // RED holds GAME buys only: GAME sells settle in GREEN.
  const hint =
    currency === "RED" ? (
      <>
        {scopeHint} {t("trades.gameSellsNote")}
      </>
    ) : (
      scopeHint
    );

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("trades.title")}
        subtitle={t("trades.subtitle")}
        coverage={scope === "subtree" ? "subtree" : "node"}
        money
      />
      <SectionCard title={t("trades.list")}>
        <TradingFilters scope={scope} account={account} onScope={(v) => set({ scope: v })} onAccount={(a) => set({ account: a })} />
        <WindowPicker key={`${w.from}-${w.to}`} window={w} now={now} onChange={(nw) => set({ from: nw?.from, to: nw?.to })} />
        <QueryBlock
          query={q}
          empty={(d) =>
            (d.trades ?? []).length === 0 ? <EmptyPage title={t("trades.empty")} hint={hint} page={d.page} onOffset={onOffset} /> : null
          }
        >
          {(d) => (
            <>
              <TradesTable trades={d.trades ?? []} />
              <Pager page={d.page} onOffset={onOffset} />
            </>
          )}
        </QueryBlock>
      </SectionCard>
    </Guarded>
  );
}
